import { z } from "zod";

export const contentStatuses = ["idea", "draft", "ready", "published"] as const;
export type ContentStatus = (typeof contentStatuses)[number];

export type SeoContent = {
  seoTitle: string;
  seoDescription: string;
};

export type RichFigureBlock =
  | {
      type: "figure";
      renderer: "component";
      id: string;
      alt: string;
      caption?: string;
    }
  | {
      type: "figure";
      renderer?: "image";
      src: string;
      id?: string;
      alt: string;
      caption?: string;
      /** Who made the picture: "Photo: …". */
      credit?: string;
    };

export interface CodeTab {
  label: string;
  language: string;
  code: string;
  filename?: string;
}

export type RichCodeBlock =
  | {
      type: "code";
      language: string;
      code: string;
      filename?: string;
      tabs?: never;
    }
  | {
      type: "code";
      tabs: CodeTab[];
      language?: never;
      code?: never;
      filename?: never;
    };

export type RichTableBlock = {
  type: "table";
  caption: string;
  columns: string[];
  rows: string[][];
};

export type RichContentBlock =
  | { type: "paragraph"; text: string }
  | { type: "heading"; level: 2 | 3; text: string }
  | RichCodeBlock
  | RichTableBlock
  | { type: "list"; items: string[] }
  | { type: "quote"; text: string; author?: string }
  | RichFigureBlock;

export type BlogPostBlock = RichContentBlock;

export type BlogPostBase = SeoContent & {
  slug: string;
  title: string;
  subtitle?: string;
  publishedAt: string;
  updatedAt?: string;
  readingTime: string;
  author?: string;
  tags: string[];
  image?: string;
  heroImage?: string;
  summary: string;
  content: BlogPostBlock[];
};

export type BlogPostStatus = (typeof contentStatuses)[number];

export type IdeaBlogPostRecord = BlogPostBase & { status: "idea" };
export type DraftBlogPostRecord = BlogPostBase & { status: "draft" };
export type ReadyBlogPostRecord = BlogPostBase & { status: "ready" };
export type PublishedBlogPostRecord = BlogPostBase & {
  status: "published";
  image: string;
};

export type BlogPostRecord =
  | IdeaBlogPostRecord
  | DraftBlogPostRecord
  | ReadyBlogPostRecord
  | PublishedBlogPostRecord;

export type BlogPostInput =
  | Omit<IdeaBlogPostRecord, "readingTime">
  | Omit<DraftBlogPostRecord, "readingTime">
  | Omit<ReadyBlogPostRecord, "readingTime">
  | Omit<PublishedBlogPostRecord, "readingTime">;

// --- Zod Schemas for Runtime Validation ---

export const contentStatusSchema = z.enum(contentStatuses);

export const codeTabSchema = z.object({
  label: z.string(),
  language: z.string(),
  code: z.string(),
  filename: z.string().optional(),
});

export const richFigureBlockSchema = z.discriminatedUnion("renderer", [
  z.object({
    type: z.literal("figure"),
    renderer: z.literal("component"),
    id: z.string(),
    alt: z.string(),
    caption: z.string().optional(),
  }),
  z.object({
    type: z.literal("figure"),
    renderer: z.literal("image").optional(),
    src: z.string(),
    id: z.string().optional(),
    alt: z.string(),
    caption: z.string().optional(),
    credit: z.string().optional(),
  }),
]);

export const richCodeBlockSchema = z.union([
  z.object({
    type: z.literal("code"),
    language: z.string(),
    code: z.string(),
    filename: z.string().optional(),
  }),
  z.object({
    type: z.literal("code"),
    tabs: z.array(codeTabSchema).min(1),
  }),
]);

export const richTableBlockSchema = z.object({
  type: z.literal("table"),
  caption: z.string(),
  columns: z.array(z.string()).min(1),
  rows: z.array(z.array(z.string())),
});

export const richContentBlockSchema = z.union([
  z.object({
    type: z.literal("paragraph"),
    text: z.string(),
  }),
  z.object({
    type: z.literal("heading"),
    level: z.union([z.literal(2), z.literal(3)]),
    text: z.string(),
  }),
  richCodeBlockSchema,
  richTableBlockSchema,
  z.object({
    type: z.literal("list"),
    items: z.array(z.string()).min(1),
  }),
  z.object({
    type: z.literal("quote"),
    text: z.string(),
    author: z.string().optional(),
  }),
  richFigureBlockSchema,
]);

export const blogPostFrontmatterSchema = z.strictObject({
  slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  title: z.string().min(1),
  subtitle: z.string().optional(),
  publishedAt: z.iso.date(),
  updatedAt: z.iso.date().optional(),
  status: contentStatusSchema,
  author: z.string().optional(),
  tags: z.array(z.string()).min(1),
  seoTitle: z.string().min(1),
  seoDescription: z.string().min(1),
  image: z.string().optional(),
  heroImage: z.string().optional(),
  summary: z.string().min(1),
});
